import clsx from 'clsx'
import React, { useState } from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate, ViewSlot } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import { MenuItem, Paper, TextField } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { DataGrid, GridColDef, GridValueFormatterParams } from '@mui/x-data-grid'
import { DatePicker } from '@mui/x-date-pickers'
import { formatDate } from 'date-fns'
import dayjs, { Dayjs } from 'dayjs'
import { DefaultLabelFormatterCallbackParams, LegendComponentOption, SeriesOption } from 'echarts'
import EChartsReact, { EChartsOption } from 'src/components/echarts'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import SerasaHistoryScope from '../serasa-history.scopes'
import { SerasaAuditCall, SerasaAuditStatistics } from '../serasa-history.types'

const labelOption = {
    show: true,
    formatter: '{b} = {c} ({d}%)',
    fontWeight: 'bold',
    rich: {
        name: {}
    }
}

const statusChartOptions = createChart('Consultas por status')
const urlChartOptions = createChart('Consultas por url')

const LOG = Logger.get('SERASA-HISTORY-VIEW')

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    syncButton: {
        alignSelf: 'center'
    }
})

const historyColumns: GridColDef[] = static_build_columns()

type SerasaHistoryViewProps = {
    className?: string
    scope: SerasaHistoryScope
}

class SerasaHistoryViewClass implements FCClassContext<SerasaHistoryViewProps> {
    // :: Fields
    scope!: SerasaHistoryScope
    sytles!: ReturnType<typeof useStyles>

    statusInputRef!: React.MutableRefObject<HTMLInputElement | undefined>
    endpointInputRef!: React.MutableRefObject<HTMLInputElement | undefined>

    // :: emissors

    readonly emitOnFind = (dataInicial: Dayjs | null, dataFinal: Dayjs | null) => {
        const status = this.statusInputRef.current?.value || undefined
        const endpoint = this.endpointInputRef.current?.value || undefined

        this.scope
            .onFind({
                startDate: dataInicial?.toDate(),
                endDate: dataFinal?.toDate(),
                status: status,
                endpoint: endpoint
            })
            .catch(LOG.caught)
    }

    // :: Methods

    onSyncState({ scope }: SerasaHistoryViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: SerasaHistoryViewProps) {
        this.scope = bindUpdate(React, scope)

        this.statusInputRef = React.useRef<HTMLInputElement>()
        this.endpointInputRef = React.useRef<HTMLInputElement>()

        const [dataInicialSelecionada, setDataInicialSelecionada] = useState<Dayjs | null>(dayjs(this.scope.startDate))
        const [dataFinalSelecionada, setDataFinalSelecionada] = useState<Dayjs | null>(dayjs(this.scope.endDate))

        updateSerie(statusChartOptions, scope.statusStatistics)
        updateSerie(urlChartOptions, scope.urlStatistics)

        const classes = this.sytles.classes

        if (scope.waiting) {
            return <ViewSlot scope={scope.waiting} />
        }

        return (
            <div className={clsx(classes.view, className)}>
                <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                    <DatePicker
                        label="Data Inicial"
                        value={dataInicialSelecionada}
                        onChange={(novaData) => setDataInicialSelecionada(novaData)}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                    <DatePicker
                        label="Data Final"
                        value={dataFinalSelecionada}
                        onChange={(novaData) => setDataFinalSelecionada(novaData)}
                        slotProps={{ textField: { fullWidth: true } }}
                    />
                    <TextField
                        select
                        inputRef={this.statusInputRef}
                        defaultValue={this.scope.status}
                        fullWidth
                        label="Status"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {this.scope.availableStatus.map((status) => (
                            <MenuItem key={status.status} value={status.status}>
                                {status.status} - {status.message}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        inputRef={this.endpointInputRef}
                        defaultValue={this.scope.endpoint}
                        fullWidth
                        label="Endpoint"
                    >
                        <MenuItem value="">Todos</MenuItem>
                        {this.scope.availableEndpoints.map((status) => (
                            <MenuItem key={status.url} value={status.url}>
                                {status.url}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Button
                        variant="outlined"
                        onClick={() => this.emitOnFind(dataInicialSelecionada, dataFinalSelecionada)}
                    >
                        Buscar
                    </Button>
                </Box>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <Box display="flex" flexDirection="row" alignItems="baseline" gap="10px">
                        <EChartsReact option={statusChartOptions} style={{ height: 400, width: '50%' }} />
                        <EChartsReact option={urlChartOptions} style={{ height: 400, width: '50%' }} />
                    </Box>
                    <div style={{ height: 460, width: '100%' }}>
                        <DataGrid
                            rows={this.scope.data}
                            columns={historyColumns}
                            getRowId={static_getRowId}
                            density="compact"
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}
                        />
                    </div>
                </Paper>
            </div>
        )
    }
}

export const SerasaHistoryView = classToFComponent(SerasaHistoryViewClass, React)
export default SerasaHistoryView

function static_build_columns(): GridColDef[] {
    return [
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 80
        },
        {
            field: 'message',
            headerName: 'Mensagem',
            minWidth: 200,
            flex: 0.5
        },
        {
            field: 'method',
            headerName: 'Método',
            minWidth: 80
        },
        {
            field: 'url',
            headerName: 'URL',
            minWidth: 200,
            flex: 1
        },
        {
            field: 'data',
            headerName: 'Data',
            minWidth: 120,
            align: 'center',
            headerAlign: 'center',
            valueFormatter: (params: GridValueFormatterParams<string>) => {
                return !params.value ? '' : formatDate(params.value, 'dd/MM/yyyy HH:mm')
            }
        }
    ]
}

function static_getRowId(row: SerasaAuditCall) {
    return '' + row.url + row.data
}

function createChart(title: string): EChartsOption {
    const donutSerie = {
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        label: {
            show: true,
            position: 'outside',
            formatter: '{b} = {c} ({d}%)'
        },
        labelLine: {
            show: true
        },
        emphasis: {
            label: labelOption
        },
        data: [] as { value: number; name: string }[]
    }

    const chartOptions = {
        legend: {
            orient: 'vertical',
            x: 'left',
            data: [] as string[]
        },
        series: donutSerie,
        title: {
            text: title,
            left: 'right',
            top: 'top'
        }
    } as EChartsOption

    return chartOptions
}

function updateSerie(chartOpts: EChartsOption, data: SerasaAuditStatistics[]) {
    const donutSerie = {
        type: 'pie',
        radius: ['50%', '70%'],
        avoidLabelOverlap: false,
        label: {
            show: true,
            position: 'outside',
            formatter: (params: DefaultLabelFormatterCallbackParams) => {
                const statusCode = Number(params.name ? params.name.substring(0, 3) : '')
                return `${isNaN(statusCode) ? params.name : `Status "${statusCode}"`} = ${params.value} (${
                    params.percent
                }%)`
            }
        },
        labelLine: {
            show: true
        },
        emphasis: {
            label: labelOption
        },
        data:
            data && data.length > 0
                ? data.map((stat) => ({
                      value: stat.records,
                      name: normalizeGroupUrl(stat.groupField)
                  }))
                : []
    } as SeriesOption

    chartOpts.legend = {
        orient: 'vertical',
        x: 'left',
        data: data && data.length > 0 ? data.map((stat) => normalizeGroupUrl(stat.groupField)) : []
    } as LegendComponentOption

    chartOpts.series = donutSerie
}

function normalizeGroupUrl(group: string): string {
    return !group.includes('http')
        ? group
        : group
              .replace(/https?:\/\//, '')
              .replace(/\/$/, '')
              .replace(/\.com(\.br)?$/, '')
              .slice(0, 20)
}
